/** Normalize saved-address field names from API / Redux for display. */

const STATE_NAMES = new Set([
  'india',
  'karnataka',
  'maharashtra',
  'telangana',
  'tamil nadu',
  'andhra pradesh',
  'kerala',
  'goa',
  'delhi',
  'uttar pradesh',
  'gujarat',
  'rajasthan',
  'west bengal',
  'punjab',
  'haryana',
]);

export function parseCityFromCommaSeparated(text: string): string {
  if (!text?.trim()) return '';
  const parts = text.split(',').map((s) => s.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const seg = parts[i];
    const lower = seg.toLowerCase();
    if (/^\d{4,6}$/.test(seg)) continue;
    if (STATE_NAMES.has(lower)) continue;
    if (lower.endsWith(' taluk') || lower.endsWith(' district')) continue;
    if (seg.length >= 2) return seg;
  }
  return parts[0] || '';
}

export function getAddressStreetLine(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return '';
  return String(
    addr.addressLine ??
      addr.address_line_1 ??
      addr.address_line1 ??
      addr.street ??
      '',
  ).trim();
}

export function getAddressCity(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return '';
  return String(addr.city ?? '').trim();
}

export function resolveAddressCity(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return '';
  const rawCity = getAddressCity(addr);
  const line = getAddressStreetLine(addr);
  if (rawCity && rawCity !== line && rawCity.length <= 48 && !rawCity.includes(',')) {
    return rawCity;
  }
  const fromRaw = parseCityFromCommaSeparated(rawCity);
  if (fromRaw && fromRaw.length <= 48) return fromRaw;
  return parseCityFromCommaSeparated(line) || rawCity || '';
}

export function unwrapAddressEntity(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const p = payload as Record<string, unknown>;
  const data = p.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  if (p.id != null || p.address_line_1 != null || p.addressLine != null) return p;
  return p;
}

/** NestJS list responses: `{ data: Address[] }`, `{ addresses: [] }`, or a raw array. */
export function unwrapAddressList(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[];
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (Array.isArray(p.data)) return p.data as Record<string, unknown>[];
    if (Array.isArray(p.addresses)) return p.addresses as Record<string, unknown>[];
    const nested = p.data as Record<string, unknown> | undefined;
    if (nested && Array.isArray(nested.data)) return nested.data as Record<string, unknown>[];
    if (nested && Array.isArray(nested.addresses)) return nested.addresses as Record<string, unknown>[];
  }
  return [];
}

export type AddressTypeKey = 'HOME' | 'WORK' | 'OTHER';

export function addressTypeToApiValue(type: AddressTypeKey): string {
  if (type === 'OTHER') return 'OTHERS';
  return type;
}

export function buildAddressPayloadFromLocation(
  loc: { name?: string; address?: string; latitude?: number; longitude?: number },
  userId: string,
  user?: { name?: string; phone?: string } | null,
  addressType: AddressTypeKey = 'HOME',
): Record<string, unknown> {
  const line = (loc.address || loc.name || '').trim();
  const nameHint = (loc.name || '').trim();
  const city =
    nameHint && nameHint !== line
      ? nameHint
      : resolveAddressCity({ address_line_1: line, city: nameHint });
  return {
    address_line_1: line,
    address_line_2: '-',
    user_id: userId,
    city: city || parseCityFromCommaSeparated(line),
    state: '-',
    country: 'India',
    postal_code: '',
    address_type: addressTypeToApiValue(addressType),
    location: {
      lat: String(loc.latitude ?? 0),
      long: String(loc.longitude ?? 0),
    },
    name: user?.name || 'User',
    phoneNumber: user?.phone || '',
  };
}

export function truncateAddressLineForDropdown(addr: Record<string, unknown> | null | undefined): string {
  const line = getAddressStreetLine(addr);
  const city = resolveAddressCity(addr);
  if (line && city && line !== city) {
    const short = line.length > 28 ? `${line.slice(0, 28)}…` : line;
    return `${short}, ${city}`;
  }
  if (line) {
    const parsed = parseCityFromCommaSeparated(line);
    if (parsed && parsed !== line) {
      const short = line.length > 28 ? `${line.slice(0, 28)}…` : line;
      return `${short}, ${parsed}`;
    }
    return line.length > 45 ? `${line.slice(0, 45)}…` : line;
  }
  return city || 'Select address';
}

export function normalizeAddressTypeKey(label: string | undefined): AddressTypeKey {
  const l = (label || 'HOME').toUpperCase();
  if (l === 'WORK' || l === 'OFFICE') return 'WORK';
  if (l === 'OTHER' || l === 'OTHERS') return 'OTHER';
  return 'HOME';
}

export function getAddressTypeKey(
  addr: Record<string, unknown> | null | undefined,
): AddressTypeKey | null {
  if (!addr) return null;
  return normalizeAddressTypeKey(String(addr.label ?? addr.address_type ?? ''));
}

export function formatAddressTypeLabel(label: string | undefined): string {
  const l = (label || 'Address').toUpperCase();
  if (l === 'HOME') return 'Home';
  if (l === 'WORK' || l === 'OFFICE') return 'Office';
  if (l === 'OTHER' || l === 'OTHERS') return 'Other';
  return l.length > 12 ? `${l.slice(0, 12)}...` : l;
}

export function formatDeliveryBarLabel(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return 'Select address';
  return truncateAddressLineForDropdown(addr);
}

export function mapApiAddressToState(data: Record<string, unknown>): {
  id: string;
  label: string;
  fullName: string;
  phone: string;
  addressLine: string;
  city: string;
  state: string;
  zip: string;
  lat?: string;
  long?: string;
} {
  const line = getAddressStreetLine(data);
  const city = resolveAddressCity({ ...data, address_line_1: line });
  const loc = data.location as { lat?: string; long?: string } | undefined;
  return {
    id: String(data.id ?? data._id ?? Date.now()),
    label: String(data.address_type ?? data.label ?? 'home'),
    fullName: String(data.name ?? data.fullName ?? ''),
    phone: String(data.phoneNumber ?? data.phone ?? data.phone_number ?? ''),
    addressLine: line,
    city,
    state: String(data.state ?? ''),
    zip: String(data.postal_code ?? data.zip ?? ''),
    lat: loc?.lat ?? (data.lat as string | undefined),
    long: loc?.long ?? (data.long as string | undefined),
  };
}
